import React, { createContext, useContext, useState, ReactNode } from 'react';
import { BarcodePrintModal } from './index';
import { InventoryItem } from '../../types/item';
import { BarcodeTemplate } from '../../types/ui';

interface PrintBarcodeContextType {
  printBarcode: (barcode: string, item?: InventoryItem, template?: BarcodeTemplate) => void;
}

const PrintBarcodeContext = createContext<PrintBarcodeContextType | undefined>(undefined);

/**
 * Custom hook to use the print barcode context
 */
export const usePrintBarcode = () => {
  const context = useContext(PrintBarcodeContext);
  if (context === undefined) {
    throw new Error('usePrintBarcode must be used within a PrintBarcodeProvider');
  }
  return context;
};

interface PrintBarcodeProviderProps {
  children: ReactNode;
}

/**
 * Provider component that enables barcode printing across the application
 * Handles the lifecycle of barcode printing separate from individual components
 */
export const PrintBarcodeProvider: React.FC<PrintBarcodeProviderProps> = ({ children }) => {
  const [barcodeModalOpen, setBarcodeModalOpen] = useState<boolean>(false);
  const [currentBarcode, setCurrentBarcode] = useState<string>('');
  const [currentItem, setCurrentItem] = useState<InventoryItem | undefined>(undefined);
  const [currentTemplate, setCurrentTemplate] = useState<BarcodeTemplate>(BarcodeTemplate.A);

  /**
   * Print a barcode by opening the barcode print modal
   */
  const printBarcode = (barcode: string, item?: InventoryItem, template: BarcodeTemplate = BarcodeTemplate.A) => {
    setCurrentBarcode(barcode);
    setCurrentItem(item);
    setCurrentTemplate(template);
    setBarcodeModalOpen(true);
  };

  return (
    <PrintBarcodeContext.Provider value={{ printBarcode }}>
      {children}
      
      {/* Modal is rendered outside of any specific component */}
      <BarcodePrintModal
        isOpen={barcodeModalOpen}
        onClose={() => setBarcodeModalOpen(false)}
        barcode={currentBarcode}
        item={currentItem}
        template={currentTemplate}
      />
    </PrintBarcodeContext.Provider>
  );
};

export default PrintBarcodeProvider; 